import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { fmtUSD, fmtNum, fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

function Home() {
  const [apps, setApps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/admin/applications").then((r) => setApps(r.data));
    api.get("/admin/requests").then((r) => setReqs(r.data));
    api.get("/admin/clients").then((r) => setClients(r.data));
  }, []);
  const pApps = apps.filter((a) => a.status === "pending").length;
  const pReqs = reqs.filter((r) => r.status === "pending").length;
  return (
    <div>
      <h1 className="text-4xl mb-8">Operational Overview</h1>
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Tile label="Pending Applications" value={pApps} testid="adm-pending-apps" />
        <Tile label="Pending Deposit/Withdraw" value={pReqs} testid="adm-pending-reqs" />
        <Tile label="Active Clients" value={clients.length} testid="adm-clients-count" />
        <Tile label="Total AUM (est.)" value={fmtUSD(clients.reduce((s, c) => s + c.total_value, 0))} testid="adm-total-aum" />
      </div>
      <div className="text-sm text-muted-foreground">Use the sidebar to review applications, approve deposits and withdrawals, manage clients, and backfill legacy holdings.</div>
    </div>
  );
}

const Tile = ({ label, value, testid }) => (
  <div className="stat-tile" data-testid={testid}>
    <div className="kicker mb-2">{label}</div>
    <div className="font-mono text-2xl">{value}</div>
  </div>
);

export function Applications() {
  const [apps, setApps] = useState([]);
  const [funds, setFunds] = useState([]);
  const [depositOpen, setDepositOpen] = useState(null); // app.id being deposited
  const [depForm, setDepForm] = useState({ fund_id: "", deposit_usd: "", note: "" });
  const load = () => api.get("/admin/applications").then((r) => setApps(r.data));
  useEffect(() => { load(); api.get("/funds").then((r) => setFunds(r.data)); }, []);
  const act = async (id, approve) => {
    try { await api.post(`/admin/applications/${id}/review`, { approve, reason: "" }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const openDeposit = (a) => {
    setDepositOpen(a.id);
    setDepForm({ fund_id: (a.fund_ids || [])[0] || (funds[0]?.id || ""),
                 deposit_usd: a.investment_amount || "", note: "" });
  };
  const submitDeposit = async (a) => {
    try {
      const r = await api.post("/super/onboard-deposit", {
        client_email: a.email, client_name: a.full_name,
        fund_id: depForm.fund_id, deposit_usd: Number(depForm.deposit_usd),
        note: depForm.note,
      });
      toast.success(`${r.data.action === "topup" ? "Top-up" : "New holding"} · ${r.data.units_credited.toFixed(4)} units @ NAV ${r.data.nav_used.toFixed(4)}`);
      setDepositOpen(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const setKyc = async (a, status) => {
    // Find the client user id via /admin/clients
    try {
      const cs = (await api.get("/admin/clients")).data;
      const c = cs.find((x) => x.email.toLowerCase() === a.email.toLowerCase());
      if (!c) { toast.error("Client user not found yet — approve first."); return; }
      await api.patch(`/admin/clients/${c.id}/kyc`, { kyc_status: status });
      toast.success(`KYC set to ${status}`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div>
      <h1 className="text-3xl mb-6">Applications</h1>
      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} className="surface p-5" data-testid={`app-row-${a.id}`}>
            <div className="flex justify-between mb-3">
              <div>
                <div className="text-lg">{a.full_name}</div>
                <div className="font-mono text-xs text-muted-foreground">{a.email} · {a.phone || "—"} · {a.country || "—"}</div>
              </div>
              <div className="text-right">
                <div className={`text-xs uppercase tracking-widest ${a.status === "approved" ? "pos" : a.status === "rejected" ? "neg" : "gold"}`}>{a.status}</div>
                <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(a.applied_at)}</div>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-4 text-sm mb-3">
              <div><div className="kicker text-[10px] mb-1">Amount</div><div className="font-mono">{fmtUSD(a.investment_amount)}</div></div>
              <div><div className="kicker text-[10px] mb-1">Accredited</div><div>{a.accredited ? "Yes" : "No"}</div></div>
              <div><div className="kicker text-[10px] mb-1">Funds</div><div className="font-mono text-xs">{(a.fund_ids || []).join(", ")}</div></div>
              <div><div className="kicker text-[10px] mb-1">KYC ack</div><div>{a.kyc_ack ? "Yes" : "No"}</div></div>
            </div>
            {a.suitability_notes && <div className="text-xs text-muted-foreground mb-3">"{a.suitability_notes}"</div>}
            {a.status === "approved" && a.temp_password && (
              <div className="mb-3 border border-primary/40 bg-primary/5 p-3 text-xs">
                <div className="kicker text-[10px] mb-1 gold">Temporary Password (share with client)</div>
                <code className="font-mono text-sm text-foreground">{a.temp_password}</code>
                <button type="button" data-testid={`app-copy-pw-${a.id}`}
                  onClick={() => { navigator.clipboard.writeText(a.temp_password); toast.success("Copied"); }}
                  className="ml-3 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">Copy</button>
                <div className="text-muted-foreground mt-1">Client should change this on first sign-in.</div>
              </div>
            )}
            {a.status === "pending" && (
              <div className="flex gap-2">
                <button data-testid={`app-approve-${a.id}`} onClick={() => act(a.id, true)} className="btn-gold px-4 py-2 text-xs uppercase tracking-widest">Approve</button>
                <button data-testid={`app-reject-${a.id}`} onClick={() => act(a.id, false)} className="border border-border hover:border-destructive px-4 py-2 text-xs uppercase tracking-widest">Reject</button>
              </div>
            )}
            {a.status === "approved" && (
              <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap gap-2 items-center">
                <button data-testid={`app-deposit-${a.id}`} onClick={() => openDeposit(a)}
                  className="btn-gold px-4 py-2 text-xs uppercase tracking-widest">Record Deposit</button>
                <span className="text-xs text-muted-foreground ml-2">Set KYC:</span>
                {["verified", "pending", "rejected"].map((s) => (
                  <button key={s} data-testid={`app-kyc-${a.id}-${s}`} onClick={() => setKyc(a, s)}
                    className={`text-[10px] uppercase tracking-widest border px-3 py-1 hover:bg-secondary ${s === "verified" ? "border-primary/60 text-primary" : s === "rejected" ? "border-destructive/60 text-destructive" : "border-border"}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {depositOpen === a.id && (
              <div className="mt-4 border border-primary/40 bg-card/40 p-4" data-testid={`app-deposit-form-${a.id}`}>
                <div className="kicker text-[10px] mb-3 gold">Record Wire Deposit for {a.full_name}</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">Fund</span>
                    <select value={depForm.fund_id} onChange={(e) => setDepForm({ ...depForm, fund_id: e.target.value })}
                      className="mt-1 w-full bg-secondary/40 border border-border p-2 text-sm">
                      {funds.map((f) => <option key={f.id} value={f.id}>{f.code} · {f.name}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">Deposit (USD)</span>
                    <input type="number" step="0.01" value={depForm.deposit_usd}
                      onChange={(e) => setDepForm({ ...depForm, deposit_usd: e.target.value })}
                      className="mt-1 w-full bg-secondary/40 border border-border p-2 text-sm font-mono" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">Wire Ref (optional)</span>
                    <input value={depForm.note} onChange={(e) => setDepForm({ ...depForm, note: e.target.value })}
                      className="mt-1 w-full bg-secondary/40 border border-border p-2 text-sm" />
                  </label>
                </div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`app-deposit-submit-${a.id}`} onClick={() => submitDeposit(a)}
                    className="btn-gold px-4 py-2 text-xs uppercase tracking-widest">Credit Units</button>
                  <button onClick={() => setDepositOpen(null)}
                    className="border border-border px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {apps.length === 0 && <div className="text-sm text-muted-foreground">No applications yet.</div>}
      </div>
    </div>
  );
}

export function Clients() {
  const [clients, setClients] = useState([]);
  const load = () => api.get("/admin/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);
  const setKyc = async (id, status) => {
    try { await api.patch(`/admin/clients/${id}/kyc`, { kyc_status: status });
      toast.success(`KYC → ${status}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div>
      <h1 className="text-3xl mb-6">Clients</h1>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest">
          <tr><th className="text-left pb-3">Name</th><th className="text-left pb-3">Email</th><th className="text-left pb-3">KYC</th><th className="text-right pb-3">Funds</th><th className="text-right pb-3">Total Value</th><th className="text-left pb-3 pl-6">Onboarded</th></tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <tr key={c.id} className="border-t border-border row-hover" data-testid={`cli-row-${c.id}`}>
              <td className="py-3">{c.name}</td>
              <td className="py-3 font-mono text-xs">{c.email}</td>
              <td className="py-3">
                <select value={c.kyc_status || "pending"}
                  data-testid={`cli-kyc-${c.id}`}
                  onChange={(e) => setKyc(c.id, e.target.value)}
                  className={`bg-transparent border border-border px-2 py-1 text-xs uppercase tracking-widest ${c.kyc_status === "verified" ? "text-primary" : c.kyc_status === "rejected" ? "text-destructive" : "text-muted-foreground"}`}>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </td>
              <td className="py-3 text-right font-mono">{c.holdings.length}</td>
              <td className="py-3 text-right font-mono">{fmtUSD(c.total_value)}</td>
              <td className="py-3 pl-6 font-mono text-xs">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Requests() {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/admin/requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, approve) => {
    try { await api.post(`/admin/requests/${id}/action`, { approve, reason: "" }); toast.success(approve ? "Approved & ledger posted." : "Rejected"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div>
      <h1 className="text-3xl mb-6">Deposit / Withdrawal Queue</h1>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest">
          <tr><th className="text-left pb-3">When</th><th className="text-left pb-3">Client</th><th className="text-left pb-3">Kind</th><th className="text-left pb-3">Fund</th><th className="text-right pb-3">Amount</th><th className="text-right pb-3">Units</th><th className="text-left pb-3 pl-6">Status</th><th className="text-right pb-3">Actions</th></tr>
        </thead>
        <tbody>
          {reqs.map((r) => (
            <tr key={r.id} className="border-t border-border row-hover" data-testid={`req-row-${r.id}`}>
              <td className="py-3 font-mono text-xs">{fmtDateTime(r.created_at)}</td>
              <td className="py-3 font-mono text-xs">{r.client_email}</td>
              <td className="py-3 uppercase text-xs">{r.kind}</td>
              <td className="py-3 font-mono text-xs">{r.fund_id}</td>
              <td className="py-3 text-right font-mono">{r.amount ? fmtUSD(r.amount) : "—"}</td>
              <td className="py-3 text-right font-mono">{r.units ? fmtNum(r.units, 4) : "—"}</td>
              <td className={`py-3 pl-6 uppercase text-xs ${r.status === "approved" ? "pos" : r.status === "rejected" ? "neg" : "gold"}`}>{r.status}</td>
              <td className="py-3 text-right">
                {r.status === "pending" && (
                  <div className="inline-flex gap-2">
                    <button data-testid={`req-approve-${r.id}`} onClick={() => act(r.id, true)} className="btn-gold px-3 py-1 text-xs uppercase">Approve</button>
                    <button data-testid={`req-reject-${r.id}`} onClick={() => act(r.id, false)} className="border border-border hover:border-destructive px-3 py-1 text-xs uppercase">Reject</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Legacy() {
  const [funds, setFunds] = useState([]);
  const [f, setF] = useState({
    client_email: "", client_name: "", fund_id: "",
    original_deposit: 100000, subscription_start_date: "2024-01-01",
    units: 100, high_water_mark_nav: 1000, next_withdrawal_date: "",
  });
  useEffect(() => { api.get("/funds").then((r) => { setFunds(r.data); setF((x) => ({ ...x, fund_id: r.data[0]?.id || "" })); }); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/legacy-holding", f); toast.success("Legacy holding backfilled."); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl mb-2">Legacy Client Onboarding</h1>
      <p className="text-sm text-muted-foreground mb-6">Backfill an existing client's holding as of a historical subscription date. Creates the user (if missing) and records an opening subscription ledger entry.</p>
      <form onSubmit={submit} className="surface p-6 grid md:grid-cols-2 gap-4">
        <F label="Client email"><input required type="email" value={f.client_email} onChange={(e) => setF({...f, client_email: e.target.value})} className="fld" data-testid="leg-email" /></F>
        <F label="Client name"><input required value={f.client_name} onChange={(e) => setF({...f, client_name: e.target.value})} className="fld" data-testid="leg-name" /></F>
        <F label="Fund"><select value={f.fund_id} onChange={(e) => setF({...f, fund_id: e.target.value})} className="fld" data-testid="leg-fund">{funds.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></F>
        <F label="Original deposit (USD)"><input required type="number" value={f.original_deposit} onChange={(e) => setF({...f, original_deposit: Number(e.target.value)})} className="fld font-mono" data-testid="leg-deposit" /></F>
        <F label="Subscription start"><input required type="date" value={f.subscription_start_date} onChange={(e) => setF({...f, subscription_start_date: e.target.value})} className="fld font-mono" data-testid="leg-start" /></F>
        <F label="Units issued"><input required type="number" step="0.0001" value={f.units} onChange={(e) => setF({...f, units: Number(e.target.value)})} className="fld font-mono" data-testid="leg-units" /></F>
        <F label="High-Water Mark (NAV)"><input required type="number" step="0.0001" value={f.high_water_mark_nav} onChange={(e) => setF({...f, high_water_mark_nav: Number(e.target.value)})} className="fld font-mono" data-testid="leg-hwm" /></F>
        <F label="Next withdrawal date"><input type="date" value={f.next_withdrawal_date} onChange={(e) => setF({...f, next_withdrawal_date: e.target.value})} className="fld font-mono" data-testid="leg-nextw" /></F>
        <div className="md:col-span-2">
          <button data-testid="leg-submit" className="btn-gold w-full py-3 text-xs uppercase tracking-widest">Backfill Holding</button>
        </div>
        <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
      </form>
    </div>
  );
}
const F = ({ label, children }) => (<label className="block"><span className="kicker text-[10px] block mb-2">{label}</span>{children}</label>);

export default function AdminPortal() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="applications" element={<Applications />} />
      <Route path="clients" element={<Clients />} />
      <Route path="requests" element={<Requests />} />
      <Route path="legacy" element={<Legacy />} />
    </Routes>
  );
}
